"use client";

import { useMemo } from "react";
import dynamic from "next/dynamic";
import { Box, CardContent, Divider, Typography, useMediaQuery } from "@mui/material";
import type { EChartsOption } from "echarts";
import type { ClauseRow } from "../types";
import {
  getClauseGroupDisplayName,
  getClauseGroupDistribution,
} from "./ClauseGroupTreemap";

const ReactECharts = dynamic(() => import("echarts-for-react"), { ssr: false });

type ClauseGroupPieChartProps = {
  rows: ClauseRow[];
  title?: string;
  palette?: string[];
};

const PIE_PALETTE = [
  "#5470C6", // blue
  "#EE6666", // red
  "#FAC858", // yellow
  "#73C0DE", // light blue
  "#9A60B4", // purple
  "#3BA272", // green
  "#FC8452", // orange
];

const OTHERS_COLOR = "#8CC078";
const TOP_GROUP_COUNT = 7;

export default function ClauseGroupPieChart({
  rows,
  title = "Incidence of Key Terms",
  palette = PIE_PALETTE,
}: ClauseGroupPieChartProps) {
  const isMobile = useMediaQuery("(max-width:768px)");

  const option = useMemo<EChartsOption>(() => {
    const { items, totalDocs } = getClauseGroupDistribution(
      rows,
      "clause_group",
    );

    const topGroups = items.slice(0, TOP_GROUP_COUNT).map((item, index) => ({
      name: item.name,
      value: item.value,
      itemStyle: { color: palette[index % palette.length] },
    }));

    const othersValue = items
      .slice(TOP_GROUP_COUNT)
      .reduce((sum, item) => sum + item.value, 0);

    const data =
      othersValue > 0
        ? [
            ...topGroups,
            {
              name: "Other",
              value: othersValue,
              itemStyle: { color: OTHERS_COLOR },
            },
          ]
        : topGroups;

    const mobileLegendData = [...data]
      .sort((a, b) =>
        getClauseGroupDisplayName(a.name ?? "").localeCompare(
          getClauseGroupDisplayName(b.name ?? ""),
        ),
      )
      .map((item) => item.name);

    const desktopLegendData = data.map((item) => item.name);
    const desktopLegendRows = {
      first: desktopLegendData.slice(0, 4),
      second: desktopLegendData.slice(4, 8),
    };

    return {
      tooltip: {
        trigger: "item",
        formatter: (params: unknown) => {
          const p = params as { name?: string; value?: number };
          const value = p.value ?? 0;
          const displayName = getClauseGroupDisplayName(p.name ?? "");
          const proportion = totalDocs > 0 ? (value / totalDocs) * 100 : 0;
          return `${displayName}<br/><b>${value} documents</b><br/>(${proportion.toFixed(1)}%)`;
        },
      },
      legend: isMobile
        ? {
            formatter: (name: string) => getClauseGroupDisplayName(name),
            orient: "vertical",
            left: "center",
            top: "48%",
            bottom: 0,
            width: "92%",
            data: mobileLegendData,
            itemWidth: 18,
            itemHeight: 10,
            itemGap: 14,
            textStyle: {
              fontSize: 14,
              fontWeight: 500,
              color: "#4b5563",
            },
          }
        : [
            {
              formatter: (name: string) => getClauseGroupDisplayName(name),
              left: "center",
              width: "70%",
              bottom: 24,
              orient: "horizontal",
              itemWidth: 18,
              itemHeight: 10,
              itemGap: 14,
              textStyle: {
                fontSize: 14,
                fontWeight: 500,
                color: "#4b5563",
              },
              data: desktopLegendRows.first,
            },
            {
              formatter: (name: string) => getClauseGroupDisplayName(name),
              left: "center",
              width: "70%",
              bottom: 0,
              orient: "horizontal",
              itemWidth: 18,
              itemHeight: 10,
              itemGap: 14,
              textStyle: {
                fontSize: 14,
                fontWeight: 500,
                color: "#4b5563",
              },
              data: desktopLegendRows.second,
            },
          ],
      series: [
        {
          type: "pie",
          radius: ["42%", "70%"],
          center: ["47%", "50%"],
          bottom: 70,
          avoidLabelOverlap: true,
          label: {
            show: true,
            formatter: (params: unknown) => {
              const p = params as { name?: string; percent?: number };
              //   const percent = Number(p.percent ?? 0);
              //   return `${getClauseGroupDisplayName(p.name ?? "")}\n${percent}%`;
              return `${getClauseGroupDisplayName(p.name ?? "")}`;
            },

            fontSize: 14,
            fontWeight: 500,
            color: "#5b5f65",
            distance: 10,
            lineHeight: 16,
            padding: [2, 4],
          },
          labelLine: { length: 10, length2: 20 },
          data,
          emphasis: {
            scale: true,
            scaleSize: 5,
          },
        },
      ],
      media: [
        {
          query: { maxWidth: 768 },
          option: {
            series: [
              {
                type: "pie",
                center: ["50%", "26%"],
                radius: ["32%", "56%"],
                bottom: 0,
                label: {
                  show: false,
                },
                labelLine: {
                  show: false,
                },
                emphasis: {
                  label: {
                    show: false,
                  },
                  labelLine: {
                    show: false,
                  },
                },
              },
            ],
          },
        },
      ],
    } as EChartsOption;
  }, [isMobile, palette, rows]);

  return (
    <CardContent sx={{ mb: "1rem" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 0,
          mb: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#363636" }}>
          {title}
        </Typography>
      </Box>
      <Divider sx={{ mb: 2 }} />
      <ReactECharts
        option={option}
        style={{ height: isMobile ? 460 : 420, width: "100%" }}
        notMerge
      />
    </CardContent>
  );
}
